import * as React from 'react';
import { createPortal } from 'react-dom';
import type { StoryCard } from '@org/model';
import { cn } from '../../../utils';
import { StoryClosingPage } from './closing-page';
import { formatStoryLabel, type StoryLabels } from './labels';
import { StoryRevealPage, storyRevealKicker } from './reveal-page';
import { StoriesStyles } from './stories-styles';
import {
  STORIES_ROOT_CLASS,
  STORY_STAGE_HEIGHT,
  STORY_STAGE_WIDTH,
  storyPageTheme,
  storyPosterStyle,
} from './stories-theme';
import { StoryIcon, StoryPortrait } from './story-bits';
import { StoryCardBody, storyStageProps } from './story-card-view';
import type {
  StoryLang,
  StoryRenderContext,
  StoryUnit,
  StoryVideoReport,
  StoryViewerMode,
  StoryWoman,
} from './story-types';
import {
  buildStoryPages,
  STORY_CARD_LABEL_KEYS,
  storyCardTitle,
  storyPageDuration,
  type StoryPage,
} from './story-utils';

export interface StoryViewerProps {
  /** the woman, as `progress.getUnitStory().woman` returns her */
  woman: StoryWoman;
  /** the free cards, in order (the reveal and closing pages are added by the viewer) */
  cards: StoryCard[];
  unit?: StoryUnit | null;
  /** the unit's poster colour on the wall (0–3). Default 0. */
  posterStyleIndex?: number;
  /** language of the story text — the app's language. There is no switch inside the story. */
  lang: StoryLang;
  labels: StoryLabels;
  /**
   * 'first'  — first meeting: animated reveal, pages advance by themselves.
   * 'reread' — opened again from her portrait: starts at the first card, no auto-advance.
   * 'preview' — admin preview: no auto-advance, no animation, closing buttons do nothing.
   */
  mode: StoryViewerMode;
  /** true = renders inside its parent box (admin live preview) instead of full-screen */
  embedded?: boolean;
  /** page to show: 0 = reveal, 1…n = cards, n+1 = closing. Changing it later jumps there. */
  initialIndex?: number;
  /** called when the person navigates (not when `initialIndex` moves the page) */
  onIndexChange?: (index: number) => void;
  onClose: () => void;
  /** fired once, the first time the person moves past the reveal page */
  onSeen?: () => void;
  onStartClass?: () => void;
  onViewMural?: () => void;
}

/** px: below this viewport width the story fills the screen; above, it is framed like a phone. */
const DESKTOP_MIN_WIDTH = 768;
/** px of design height the phone layout always guarantees (shorter screens scale down). */
const PHONE_MIN_DESIGN_HEIGHT = 700;
/** prototype desktop frame: the stage sits between two 145.5px gutters that hold the side arrows */
const DESKTOP_GUTTER = 145.5;
const HOLD_TO_PAUSE_MS = 240;
const AFTER_VIDEO_MS = 1500;
const SWIPE_CLOSE_PX = 70;
const SWIPE_START_PX = 12;

const REDUCED_QUERY = '(prefers-reduced-motion: reduce)';
function queryReducedMotion(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia(REDUCED_QUERY).matches
    : false;
}
function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = React.useState(queryReducedMotion);
  React.useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia(REDUCED_QUERY);
    const onChange = () => setReduced(mq.matches);
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);
  return reduced;
}

interface StoryLayout {
  desktop: boolean;
  scale: number;
  wrap?: React.CSSProperties;
  scaleBox: React.CSSProperties;
}

/** Sizes the 390×844 design page to the screen (full-screen) or to the parent box (embedded). */
function useStoryLayout(rootRef: React.RefObject<HTMLDivElement | null>, embedded: boolean): StoryLayout {
  const [box, setBox] = React.useState<{ w: number; h: number }>(() =>
    !embedded && typeof window !== 'undefined' ? { w: window.innerWidth, h: window.innerHeight } : { w: 0, h: 0 },
  );
  React.useLayoutEffect(() => {
    if (embedded) {
      const el = rootRef.current;
      if (!el) return;
      const measure = () => setBox({ w: el.clientWidth, h: el.clientHeight });
      measure();
      if (typeof ResizeObserver === 'undefined') return;
      const ro = new ResizeObserver(measure);
      ro.observe(el);
      return () => ro.disconnect();
    }
    const measure = () => setBox({ w: window.innerWidth, h: window.innerHeight });
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [embedded, rootRef]);

  const { w, h } = box;
  if (embedded) {
    const scale = w > 0 && h > 0 ? Math.min(w / STORY_STAGE_WIDTH, h / STORY_STAGE_HEIGHT) : 1;
    return {
      desktop: false,
      scale,
      scaleBox: {
        left: w > 0 ? (w - STORY_STAGE_WIDTH * scale) / 2 : 0,
        top: h > 0 ? (h - STORY_STAGE_HEIGHT * scale) / 2 : 0,
        transform: `scale(${scale})`,
      },
    };
  }
  if (w >= DESKTOP_MIN_WIDTH) {
    const height = Math.max(320, Math.min(STORY_STAGE_HEIGHT, h - 96));
    const scale = height / STORY_STAGE_HEIGHT;
    return {
      desktop: true,
      scale,
      wrap: { width: STORY_STAGE_WIDTH * scale + DESKTOP_GUTTER * 2, height },
      scaleBox: { left: DESKTOP_GUTTER, transform: `scale(${scale})` },
    };
  }
  const scale = w > 0 && h > 0 ? Math.min(w / STORY_STAGE_WIDTH, h / PHONE_MIN_DESIGN_HEIGHT) : 1;
  return {
    desktop: false,
    scale,
    scaleBox:
      w > 0 && h > 0
        ? { width: w / scale, height: h / scale, transform: `scale(${scale})` }
        : { width: '100%', height: '100%' },
  };
}

const FOCUSABLE = 'button:not([disabled]):not([tabindex="-1"]), video[controls], iframe, a[href]';

/**
 * "Ela chegou" — the story of the woman of a unit, in the Stories format
 * every student already knows: tap right / left, hold to pause, swipe down to
 * close. Pages = a fixed reveal page + the woman's cards + a fixed closing page.
 * Pure props-driven: no tRPC, no i18n hooks.
 */
export function StoryViewer({
  woman,
  cards,
  unit,
  posterStyleIndex,
  lang,
  labels,
  mode,
  embedded = false,
  initialIndex,
  onIndexChange,
  onClose,
  onSeen,
  onStartClass,
  onViewMural,
}: StoryViewerProps) {
  const pages = React.useMemo(() => buildStoryPages(cards), [cards]);
  const last = pages.length - 1;
  const clamp = (i: number) => Math.max(0, Math.min(last, Math.trunc(Number.isFinite(i) ? i : 0)));

  const reduced = usePrefersReducedMotion();
  const autoAdvance = mode === 'first' && !reduced;

  const [rawPage, setPage] = React.useState(() => clamp(initialIndex ?? (mode === 'reread' ? 1 : 0)));
  const page = Math.min(rawPage, last);
  const [userPaused, setUserPaused] = React.useState(false);
  const [holdPaused, setHoldPaused] = React.useState(false);
  const paused = userPaused || holdPaused;
  /** reveal animation: 0 = not played yet, 1 = her name is out, 2 = finished */
  const [revealStep, setRevealStep] = React.useState<0 | 1 | 2>(0);
  const revealAnimated = mode === 'first' && !reduced && revealStep < 2;

  const rootRef = React.useRef<HTMLDivElement | null>(null);
  const stageRef = React.useRef<HTMLDivElement | null>(null);
  const fillRef = React.useRef<HTMLElement | null>(null);
  const pauseBtnRef = React.useRef<HTMLButtonElement | null>(null);
  const closeBtnRef = React.useRef<HTMLButtonElement | null>(null);
  const elapsed = React.useRef(0);
  const video = React.useRef<StoryVideoReport>({ kind: 'none', state: 'idle', fraction: 0 });
  const seen = React.useRef(false);
  const suppressClick = React.useRef(false);
  const pointer = React.useRef<{ y: number; dy: number } | null>(null);
  const holdTimer = React.useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const layout = useStoryLayout(rootRef, embedded);

  // Latest values for the long-lived listeners / animation loop.
  const live = React.useRef({ page, pages, paused, autoAdvance, woman, lang, last, scale: layout.scale, onClose, onSeen, onIndexChange });
  live.current = { page, pages, paused, autoAdvance, woman, lang, last, scale: layout.scale, onClose, onSeen, onIndexChange };

  const show = React.useCallback((index: number) => {
    elapsed.current = 0;
    video.current = { kind: 'none', state: 'idle', fraction: 0 };
    live.current.page = index;
    if (index > 0) setRevealStep(2);
    setPage(index);
  }, []);

  /** Navigation by the person (tap, arrows, keyboard, auto-advance). */
  const goTo = React.useCallback(
    (index: number) => {
      const s = live.current;
      if (index < 0 || index > s.last || index === s.page) return;
      show(index);
      if (index > 0 && !seen.current) {
        seen.current = true;
        s.onSeen?.();
      }
      s.onIndexChange?.(index);
    },
    [show],
  );
  const go = React.useCallback((delta: number) => goTo(live.current.page + delta), [goTo]);

  // The parent moved `initialIndex` (admin: "show me the card I am editing").
  const firstIndexRun = React.useRef(true);
  React.useEffect(() => {
    if (firstIndexRun.current) {
      firstIndexRun.current = false;
      return;
    }
    if (initialIndex === undefined || initialIndex === null) return;
    const target = Math.max(0, Math.min(live.current.last, Math.trunc(initialIndex)));
    if (target !== live.current.page) show(target);
  }, [initialIndex, show]);

  // Cards were removed under us: stay on a page that exists.
  React.useEffect(() => {
    if (rawPage > last) setPage(last);
  }, [rawPage, last]);

  // Reveal choreography milestones (the CSS does the motion).
  React.useEffect(() => {
    if (page !== 0 || !revealAnimated) return;
    const named = setTimeout(() => setRevealStep((s) => (s < 1 ? 1 : s)), 1700);
    const done = setTimeout(() => setRevealStep(2), 3200);
    return () => {
      clearTimeout(named);
      clearTimeout(done);
    };
    // revealStep is deliberately not a dependency: the timers run once per visit.
  }, [page, mode, reduced]);

  // Progress + auto-advance loop. Writes the segment width straight to the DOM.
  React.useEffect(() => {
    if (typeof requestAnimationFrame !== 'function') return;
    let raf = 0;
    let lastTs = 0;
    const tick = (ts: number) => {
      const dt = lastTs ? Math.min(100, ts - lastTs) : 0;
      lastTs = ts;
      const s = live.current;
      const pg: StoryPage | undefined = s.pages[s.page];
      const fill = fillRef.current;
      let width = 100;
      if (pg && pg.kind === 'card' && pg.card.type === 'video') {
        const v = video.current;
        if (v.kind === 'native') {
          width = v.fraction * 100;
          if (v.state === 'ended' && s.autoAdvance && !s.paused) {
            elapsed.current += dt;
            if (elapsed.current > AFTER_VIDEO_MS) goTo(s.page + 1);
          }
        }
      } else if (pg && pg.kind !== 'closing' && s.autoAdvance) {
        if (!s.paused) elapsed.current += dt;
        const d = storyPageDuration(pg, s.woman, s.lang);
        width = Math.min(100, (elapsed.current / d) * 100);
        if (elapsed.current >= d) goTo(s.page + 1);
      }
      if (fill) fill.style.width = `${width}%`;
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [goTo]);

  // Full-screen only: lock the page behind, take focus, give it back on close.
  React.useEffect(() => {
    if (embedded) return;
    const previous = document.activeElement as HTMLElement | null;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    (pauseBtnRef.current ?? closeBtnRef.current)?.focus({ preventScroll: true });
    return () => {
      document.body.style.overflow = overflow;
      if (previous && typeof previous.focus === 'function' && document.contains(previous)) {
        previous.focus({ preventScroll: true });
      }
    };
  }, [embedded]);

  const handleKey = React.useCallback(
    (e: KeyboardEvent | React.KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName ?? '';
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target?.isContentEditable) return;
      const s = live.current;
      if (e.key === 'Escape') {
        if (embedded) return;
        e.preventDefault();
        s.onClose();
        return;
      }
      if (e.key === 'Tab') {
        const root = rootRef.current;
        if (embedded || !root) return;
        const items = Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE));
        if (!items.length) return;
        const i = items.indexOf(document.activeElement as HTMLElement);
        if (e.shiftKey && i <= 0) {
          e.preventDefault();
          items[items.length - 1].focus();
        } else if (!e.shiftKey && (i === -1 || i === items.length - 1)) {
          e.preventDefault();
          items[0].focus();
        }
        return;
      }
      // Inside the native player the arrows and space belong to the video.
      if (tag === 'VIDEO') return;
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        go(1);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        go(-1);
      } else if (e.key === ' ' && tag !== 'BUTTON') {
        e.preventDefault();
        if (s.autoAdvance) setUserPaused((p) => !p);
      }
    },
    [embedded, go],
  );

  React.useEffect(() => {
    if (embedded) return;
    const onKey = (e: KeyboardEvent) => handleKey(e);
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [embedded, handleKey]);

  React.useEffect(() => () => clearTimeout(holdTimer.current), []);

  /* pointer: tap = navigate, hold = pause, swipe down = close */
  const onZonePointerDown = (e: React.PointerEvent) => {
    suppressClick.current = false;
    pointer.current = { y: e.clientY, dy: 0 };
    clearTimeout(holdTimer.current);
    holdTimer.current = setTimeout(() => {
      suppressClick.current = true;
      setHoldPaused(true);
    }, HOLD_TO_PAUSE_MS);
  };
  const onStagePointerMove = (e: React.PointerEvent) => {
    const pd = pointer.current;
    const stage = stageRef.current;
    if (!pd || !stage) return;
    pd.dy = (e.clientY - pd.y) / (live.current.scale || 1);
    if (pd.dy > SWIPE_START_PX) {
      clearTimeout(holdTimer.current);
      if (!embedded) {
        stage.style.transform = `translateY(${Math.min(160, pd.dy * 0.6)}px)`;
        stage.style.opacity = String(1 - Math.min(0.35, pd.dy / 600));
      }
    }
  };
  const onStagePointerEnd = () => {
    const pd = pointer.current;
    if (!pd) return;
    clearTimeout(holdTimer.current);
    pointer.current = null;
    if (!embedded && pd.dy > SWIPE_CLOSE_PX) {
      suppressClick.current = true;
      live.current.onClose();
      return;
    }
    const stage = stageRef.current;
    if (stage) {
      stage.style.transform = '';
      stage.style.opacity = '';
    }
    if (pd.dy > SWIPE_START_PX) suppressClick.current = true;
    setHoldPaused(false);
  };
  const onZoneClick = (delta: number) => () => {
    if (suppressClick.current) {
      suppressClick.current = false;
      return;
    }
    go(delta);
  };

  const onVideoReport = React.useCallback((report: StoryVideoReport) => {
    if (report.state !== 'ended') elapsed.current = 0;
    video.current = report;
  }, []);

  if (typeof document === 'undefined') return null;

  const pg = pages[page];
  const named = page > 0 || !revealAnimated || revealStep >= 1;
  const baseTheme = storyPageTheme(pg, posterStyleIndex);
  // Once the poster colour has flooded the reveal page, the header (segments,
  // name, buttons) must be readable on that colour, not on the teaser pink.
  const theme = pg.kind === 'reveal' && named && baseTheme.ufg ? { ...baseTheme, fg: baseTheme.ufg } : baseTheme;
  const posterStyle = storyPosterStyle(posterStyleIndex);
  const stage = storyStageProps(theme);
  const desktop = layout.desktop;
  const inert = mode === 'preview';
  const initial = woman.name.trim().charAt(0).toUpperCase();

  const pageTitle =
    pg.kind === 'reveal'
      ? storyRevealKicker(labels, unit)
      : pg.kind === 'closing'
        ? labels.closingKicker
        : storyCardTitle(pg.card, lang).text || labels[STORY_CARD_LABEL_KEYS[pg.card.type]] || '';
  const announce = formatStoryLabel(labels.announce, { i: page + 1, n: pages.length, title: pageTitle });

  const ctx: StoryRenderContext = {
    woman,
    unit,
    lang,
    labels,
    posterStyle,
    theme,
    interactive: true,
    paused,
    onVideoReport,
  };

  const showWatermark = pg.kind === 'closing' || (pg.kind === 'card' && pg.card.type !== 'video');

  const tree = (
    <div
      ref={rootRef}
      className={cn(STORIES_ROOT_CLASS, embedded ? 'zs-embedded' : 'zs-full', desktop ? 'zs-desktop' : 'zs-phone')}
      data-testid="story-viewer"
      data-page={page}
      onKeyDown={embedded ? handleKey : undefined}
    >
      <StoriesStyles />
      <div
        className={cn('zs-sv', !embedded && 'zs-open')}
        role="dialog"
        aria-modal={embedded ? undefined : true}
        aria-label={formatStoryLabel(labels.storyOf, { name: woman.name })}
      >
        <div className="zs-wrap" style={layout.wrap}>
          <div className="zs-scale" style={layout.scaleBox}>
            <div
              ref={stageRef}
              className={stage.className}
              style={stage.style}
              onPointerMove={onStagePointerMove}
              onPointerUp={onStagePointerEnd}
              onPointerCancel={onStagePointerEnd}
              onPointerLeave={onStagePointerEnd}
            >
              {showWatermark ? (
                <div className="zs-wmk" aria-hidden="true" style={{ color: theme.fg }}>
                  {String(page + 1).padStart(2, '0')}
                </div>
              ) : null}
              <div className="zs-page" key={pg.kind === 'card' ? `card-${pg.card.id}-${page}` : pg.kind}>
                {pg.kind === 'reveal' ? (
                  <StoryRevealPage
                    woman={woman}
                    unit={unit}
                    lang={lang}
                    labels={labels}
                    theme={theme}
                    reveal={revealAnimated ? 'animate' : 'instant'}
                  />
                ) : pg.kind === 'closing' ? (
                  <StoryClosingPage
                    woman={woman}
                    unit={unit}
                    lang={lang}
                    labels={labels}
                    posterStyle={posterStyle}
                    inert={inert}
                    onStartClass={onStartClass}
                    onViewMural={onViewMural}
                  />
                ) : (
                  <StoryCardBody card={pg.card} ctx={ctx} />
                )}
              </div>
              <button
                type="button"
                className="zs-zone zs-prev"
                aria-label={labels.prevPage}
                disabled={page === 0}
                onPointerDown={onZonePointerDown}
                onClick={onZoneClick(-1)}
              />
              <button
                type="button"
                className="zs-zone zs-next"
                aria-label={labels.nextPage}
                disabled={page === last}
                onPointerDown={onZonePointerDown}
                onClick={onZoneClick(1)}
              />
              <div className="zs-head">
                <div className="zs-segs" aria-hidden="true">
                  {pages.map((_, i) => (
                    <div className={cn('zs-seg', i < page && 'zs-done')} key={i}>
                      <b key={i === page ? 'current' : 'rest'} ref={i === page ? fillRef : undefined} />
                    </div>
                  ))}
                </div>
                <div className="zs-meta">
                  <div className="zs-who2">
                    <StoryPortrait
                      url={named ? woman.portraitUrl : null}
                      fallback={named ? initial : '?'}
                      color={theme.fg}
                      size={30}
                    />
                    {named ? <span className="zs-who2-name">{woman.name}</span> : null}
                    {unit ? <small>{formatStoryLabel(labels.unitN, { n: unit.order })}</small> : null}
                    {mode === 'reread' ? (
                      <span className="zs-flag" data-testid="story-flag">
                        {labels.rereading}
                      </span>
                    ) : autoAdvance && paused ? (
                      <span className="zs-flag" data-testid="story-flag">
                        {labels.paused}
                      </span>
                    ) : null}
                  </div>
                  {autoAdvance ? (
                    <button
                      type="button"
                      ref={pauseBtnRef}
                      className="zs-btn"
                      aria-label={paused ? labels.resume : labels.pause}
                      aria-pressed={userPaused}
                      onClick={() => setUserPaused((p) => !p)}
                    >
                      {userPaused ? <StoryIcon.Play /> : <StoryIcon.Pause />}
                    </button>
                  ) : null}
                  {desktop ? null : (
                    <button type="button" ref={closeBtnRef} className="zs-btn" aria-label={labels.close} onClick={onClose}>
                      <StoryIcon.X />
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
          {desktop ? (
            <>
              <button type="button" className="zs-side zs-l" aria-label={labels.prevPage} disabled={page === 0} onClick={() => go(-1)}>
                <StoryIcon.Left />
              </button>
              <button type="button" className="zs-side zs-r" aria-label={labels.nextPage} disabled={page === last} onClick={() => go(1)}>
                <StoryIcon.Right />
              </button>
            </>
          ) : null}
        </div>
        {desktop ? (
          <>
            <button type="button" ref={closeBtnRef} className="zs-x" aria-label={labels.close} onClick={onClose}>
              <StoryIcon.X />
            </button>
            <div className="zs-keys" aria-hidden="true">
              {labels.keysHint}
            </div>
          </>
        ) : null}
        <div className="zs-sr-only" aria-live="polite" data-testid="story-live">
          {announce}
        </div>
      </div>
    </div>
  );

  return embedded ? tree : createPortal(tree, document.body);
}
